import { createUseStyles } from 'react-jss';
import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import { Stack } from '@mui/material';
import key from 'weak-key';
import { Fragment } from 'react';

import { Typography } from '../../Typography';
import type { LcpnTheme } from '../../../styles/theme';
import { Icon } from '../../Icon';

import { ReactComponent as Logo } from './lcpn-logo.svg';
import type { Heading, Section } from './types';

const useStyles = createUseStyles((theme: LcpnTheme) => ({
  root: {
    color: '#333740',
    width: '100%',
    border: 'none',
    borderSpacing: 0,
    '&.is-comparison': {
      tableLayout: 'fixed',
      '@media print': {
        '& th, & td': {
          '& > *': {
            fontSize: '0.9em',
          },
          '& .comparison-code-badge': {
            fontSize: '0.9em',
          },
          '& .comparison-header-icon': {
            transform: 'scale(0.8)',
            transformOrigin: 'top center',
          },
        },
      },
    },
    '& tr': {},
    '& th, & td': {
      border: 'none',
      borderWidth: 0,
      borderBottom: '8px solid white',
      textAlign: 'left',
      verticalAlign: 'top',
      position: 'relative',

      '&.image-cell': {
        verticalAlign: 'middle',
        borderBottom: 'none',
        '& > *': {
          width: '100%',
          maxWidth: 400,
          paddingRight: theme.spacing(8),
          height: 'auto',
          '&::after': {
            display: 'none',
          },
        },
      },

      '&.header-cell': {
        borderBottom: '1px solid white',
        '&:not(:first-child)': {
          borderLeft: '1px solid white',
        },
        '& > *': {
          paddingTop: theme.spacing(4),
          paddingBottom: theme.spacing(4),
        },
      },

      '&.divider-cell': {
        height: theme.spacing(8),
      },

      '& > *': {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
        paddingLeft: theme.spacing(5),
        paddingRight: theme.spacing(5),
        display: 'block',
        overflow: 'visible',
        wordBreak: 'break-word',
        '&::after': {
          content: '""',
          borderBottom: '1px solid #6B6B6B',
          position: 'absolute',
          width: '100%',
          left: 0,
          bottom: 0,
        },
      },
      '& ol': {
        paddingLeft: theme.spacing(4),
        marginTop: 0,
        marginBottom: theme.spacing(4),
      },
    },
    '& th': {
      color: 'white',
    },
    '& td': {
      width: '33.3333%',
      '&.double-width': {
        width: '66.6666%',
      },
      '&.full-width': {
        width: '100%',
      },
      '&.title': {
        backgroundColor: 'rgba(228, 229, 230, 0.3)',
      },
      '&.to > *': {
        borderLeft: '1px solid #6B6B6B',
      },
      '&.notesTitle': {
        textAlign: 'center',
        backgroundColor: 'rgba(228, 229, 230, 0.75)',
        textTransform: 'uppercase',
      },
    },
    '@media print': {
      '&&': {
        '& th, & td': {
          wordBreak: 'break-word',
          '& > *': {
            paddingTop: theme.spacing(2),
            paddingBottom: theme.spacing(2),
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
          },
          '&.header-cell': {
            '& > *': {
              paddingTop: theme.spacing(2),
              paddingBottom: theme.spacing(2),
              paddingLeft: theme.spacing(2),
              paddingRight: theme.spacing(2),
            },
          },
        },
      },
    },
    '&.is-comparison.three-columns': {
      '@media print': {
        '& th, & td': {
          '& > *': {
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
            fontSize: '0.85em',
          },
          '& .comparison-code-badge': {
            fontSize: '0.85em',
          },
          '& .comparison-header-icon': {
            transform: 'scale(0.75)',
            transformOrigin: 'top center',
          },
        },
      },
    },
    '&.is-comparison.four-columns': {
      '@media print': {
        '& th, & td': {
          '& > *': {
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
            fontSize: '0.8em',
          },
          '& .comparison-code-badge': {
            fontSize: '0.85em',
            padding: theme.spacing(1, 1),
          },
          '& .comparison-header-icon': {
            transform: 'scale(0.7)',
            transformOrigin: 'top center',
          },
        },
      },
    },
  },
}));

interface Props<T> {
  headings: Heading<T>[];
  sections: Section<T>[];
  items: T[] | T;
  type: 'heatmap' | 'poi';
}

function isComparisonTable<T>(items: unknown): items is T[] {
  return Array.isArray(items) && items.length >= 2;
}

function isPrintTable<T>(items: unknown): items is T {
  return typeof items === 'object' && items !== null && !Array.isArray(items);
}

export function DataDisplayTable<T>(props: Props<T>) {
  const { headings, sections, items } = props;

  const classes = useStyles();

  let comparisonItems: T[];
  if (isComparisonTable<T>(items)) {
    comparisonItems = items;
  } else if (isPrintTable<T>(items)) {
    comparisonItems = [items];
  } else {
    return <></>;
  }

  const isComparison = isComparisonTable<T>(items);

  // Determine column count class for print optimization
  const columnClass =
    comparisonItems.length === 3
      ? 'three-columns'
      : comparisonItems.length === 4
      ? 'four-columns'
      : '';

  return (
    <table
      className={clsx(classes.root, {
        'is-comparison': isComparison,
        [columnClass]: columnClass,
      })}
    >
      <tbody>
        {headings.map((h, i) => {
          const getValue = (item: T) =>
            (typeof h.key === 'function'
              ? h.key(item)
              : item[h.key]) as ReactNode;

          const getColor = (
            item: T,
            type: 'color' | 'backgroundColor' = 'color',
          ) =>
            (typeof h[type] === 'function'
              ? (h[type] as Function)(item)
              : h[type]) as string;

          return (
            <tr key={key(h)}>
              {!i && isComparison && (
                <td className={clsx('image-cell')} rowSpan={headings.length}>
                  <div>
                    <Logo />
                  </div>
                </td>
              )}

              {h.mergeCells && isComparison ? (
                <td
                  colSpan={comparisonItems.length}
                  className={clsx('header-cell')}
                  style={{
                    backgroundColor: getColor(
                      comparisonItems[0],
                      'backgroundColor',
                    ),
                    color: getColor(comparisonItems[0]),
                  }}
                >
                  <Typography variant={h.typographyVariant || 'p1'}>
                    {/* Show the heading label from first item */}
                    {getValue(comparisonItems[0])}
                    {/* List all study titles */}
                    {comparisonItems.map((item, idx) => {
                      const studyTitle =
                        (item as any).studyName || (item as any).countryName;
                      const studyCode =
                        (item as any).studyAcronym || (item as any).id;
                      return (
                        <div key={idx}>
                          {studyCode} - {studyTitle}
                        </div>
                      );
                    })}
                  </Typography>
                </td>
              ) : (
                comparisonItems.map((item, itemIndex) => (
                  <td
                    key={itemIndex}
                    colSpan={isComparison ? 1 : comparisonItems.length + 1}
                    className={clsx('header-cell', {
                      'full-width': !isComparison,
                    })}
                    style={{
                      backgroundColor: getColor(item, 'backgroundColor'),
                      color: getColor(item),
                    }}
                  >
                    <Typography variant={h.typographyVariant || 'p1'}>
                      {getValue(item)}
                    </Typography>
                  </td>
                ))
              )}
            </tr>
          );
        })}
        <tr>
          <th
            colSpan={comparisonItems.length + 1}
            className={clsx('divider-cell')}
          />
        </tr>

        {sections.map(s => (
          <Fragment key={key(s)}>
            {!!s.title && (
              <tr>
                <th
                  colSpan={comparisonItems.length + 1}
                  style={{ backgroundColor: s.backgroundColor }}
                >
                  <Typography variant={'p1'}>
                    <Stack direction={'row'} spacing={1} alignItems={'center'}>
                      {!!s.icon && (
                        <Icon width={20} height={20} variant={s.icon} />
                      )}
                      <div>{s.title}</div>
                    </Stack>
                  </Typography>
                </th>
              </tr>
            )}
            {s.rows.map(r => {
              const getValue = (item: T) =>
                (typeof r.key === 'function' ? r.key(item) : item[r.key]) as
                  | string
                  | number
                  | undefined;
              const format = r.format || (v => (v || r.fallback) as string);

              // Hide row if all comparison items have no value
              if (
                isComparison &&
                comparisonItems.every(item => !getValue(item))
              ) {
                return null;
              }

              return (
                <tr key={key(r)}>
                  <td className={'title'}>
                    <Typography variant={'p1'} preserveReactNodes>
                      {r.title}
                    </Typography>
                  </td>
                  {comparisonItems.map((item, itemIndex) => (
                    <td
                      key={itemIndex}
                      className={clsx({
                        from: itemIndex === 0,
                        to: itemIndex > 0 && isComparison,
                        'double-width': !isComparison,
                      })}
                    >
                      <Typography
                        variant={r.typographyVariant || 'p1'}
                        preserveReactNodes={r.preserveReactNodes}
                      >
                        {format(getValue(item))}
                      </Typography>
                    </td>
                  ))}
                </tr>
              );
            })}
            {!!s.notes &&
              s.notes.map(n => (
                <Fragment key={key(n)}>
                  <tr>
                    <td
                      colSpan={comparisonItems.length + 1}
                      className={'notesTitle'}
                    >
                      <Typography
                        variant={n.typographyVariant || 'p3'}
                        fontWeight={900}
                      >
                        {n.title}
                      </Typography>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={comparisonItems.length + 1}>
                      <Typography variant={n.typographyVariant || 'p3'}>
                        {typeof n.body === 'function'
                          ? n.body(comparisonItems[0])
                          : n.body}{' '}
                      </Typography>
                    </td>
                  </tr>
                </Fragment>
              ))}
            <>
              <tr>
                <th
                  colSpan={comparisonItems.length + 1}
                  className={clsx('divider-cell')}
                />
              </tr>
            </>
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}
